import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Plus, Trash2, Loader2, Check } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** L'annonceur déclare tous ses numéros joignables (appel + WhatsApp) pour ses clients. */
export const MesTelephones = ({ profileId }) => {
  const [principal, setPrincipal] = useState(null);
  const [lignes, setLignes] = useState([]);
  const [enregistrement, setEnregistrement] = useState(false);
  const [max, setMax] = useState(4);

  useEffect(() => {
    axios
      .get(`${API}/profiles/${profileId}/telephones`)
      .then((r) => {
        setPrincipal(r.data.principal);
        setMax(r.data.max);
        setLignes(
          r.data.telephones
            .filter((n) => n.e164 !== r.data.principal)
            .map((n) => n.e164.replace("+225", ""))
        );
      })
      .catch(() => {});
  }, [profileId]);

  const majLigne = (i, valeur) =>
    setLignes(lignes.map((l, idx) => (idx === i ? valeur.replace(/\D/g, "").slice(0, 10) : l)));

  const enregistrer = async () => {
    setEnregistrement(true);
    try {
      const res = await axios.put(`${API}/profiles/${profileId}/telephones`, {
        telephones: lignes.filter((l) => l.trim()),
      });
      setLignes(
        res.data.telephones.filter((n) => n.e164 !== principal).map((n) => n.e164.replace("+225", ""))
      );
      toast.success("Numéros enregistrés", {
        description: "Vos clients abonnés peuvent vous appeler ou vous écrire sur WhatsApp.",
      });
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(detail?.message || detail || "Numéro invalide", { description: detail?.aide });
    } finally {
      setEnregistrement(false);
    }
  };

  return (
    <div data-testid="mes-telephones">
      <p className="text-sm text-stone-600">
        Les membres abonnés voient ces numéros sur vos annonces, avec l'appel direct et WhatsApp.
      </p>

      {principal && (
        <div className="mt-4 flex items-center gap-2 rounded-2xl bg-[#FAF8F5] border border-[#E5DFD5] px-4 py-3">
          <Phone size={15} className="text-[#0A5C36]" />
          <span className="font-heading text-sm font-bold text-stone-900" data-testid="telephone-principal">
            {principal}
          </span>
          <span className="ml-auto text-[11px] font-semibold uppercase tracking-wider text-stone-400">
            Numéro principal
          </span>
        </div>
      )}

      <div className="mt-3 space-y-2">
        {lignes.map((ligne, i) => (
          <div key={i} className="flex items-center gap-2">
            <span className="flex flex-1 items-center gap-2 rounded-2xl border-2 border-[#E5DFD5] focus-within:border-stone-900 px-4 py-2.5 transition-colors">
              <span className="text-sm font-semibold text-stone-500">+225</span>
              <input
                value={ligne}
                onChange={(e) => majLigne(i, e.target.value)}
                inputMode="numeric"
                placeholder="07 07 07 07 07"
                data-testid={`telephone-input-${i}`}
                className="w-full min-w-0 bg-transparent text-sm font-semibold tracking-wide text-stone-900 outline-none placeholder:font-normal placeholder:text-stone-300"
              />
            </span>
            <button
              onClick={() => setLignes(lignes.filter((_, idx) => idx !== i))}
              data-testid={`telephone-supprimer-${i}`}
              aria-label="Supprimer ce numéro"
              className="shrink-0 rounded-full border border-[#E5DFD5] p-2.5 text-stone-400 transition-colors hover:border-red-300 hover:text-red-600"
            >
              <Trash2 size={15} />
            </button>
          </div>
        ))}
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        <button
          onClick={() => setLignes([...lignes, ""])}
          disabled={lignes.length >= max}
          data-testid="telephone-ajouter"
          className="inline-flex items-center gap-1.5 rounded-full border-2 border-[#E5DFD5] px-4 py-2 text-xs font-bold text-stone-700 transition-colors hover:border-stone-900 disabled:opacity-40"
        >
          <Plus size={14} /> Ajouter un numéro
        </button>
        <button
          onClick={enregistrer}
          disabled={enregistrement}
          data-testid="telephone-enregistrer"
          className="inline-flex items-center gap-1.5 rounded-full bg-stone-900 px-5 py-2 text-xs font-bold text-white transition-colors hover:bg-stone-700 disabled:opacity-60"
        >
          {enregistrement ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
          Enregistrer
        </button>
      </div>
    </div>
  );
};
